import { PRINTABLE_WIDTH_PX } from '../printableWidth';
import { normalizeConditionMarkers } from './normalizeConditionMarkers';
import { clampNegativeVerticalMargins } from './clampNegativeVerticalMargins';

const PIXELS = /^(\d+(?:\.\d+)?)px$/i;
const BARE_NUMBER = /^\d+(?:\.\d+)?$/;

/**
 * Reads a pixel width declared either in the inline style or through the
 * legacy `width` attribute Word and Jodit both produce.
 * @function
 *
 * @param {Element} element - The table, cell or column to read.
 * @returns {number|null} The width in pixels, or null when none is declared in pixels.
 */
const pixelWidth = (element) => {
  const fromStyle = (element.style?.width || '').trim().match(PIXELS);
  if (fromStyle) return Number(fromStyle[1]);

  const attribute = (element.getAttribute('width') || '').trim();
  return BARE_NUMBER.test(attribute) ? Number(attribute) : null;
};

const toPercentage = (widthPx, referencePx) => {
  const ratio = Math.min(widthPx / referencePx, 1) * 100;
  return `${Math.round(ratio * 100) / 100}%`;
};

const applyPercentage = (element, percentage) => {
  element.style.setProperty('width', percentage);
  element.removeAttribute('width');
};

/**
 * Converts the pixel widths of tables and their cells into percentages.
 *
 * The editor writes the width dragged by the author in pixels, measured on its
 * own canvas. Anything wider than the printable page is clamped to 100%,
 * narrower tables keep their proportion, and cells are expressed relative to
 * their table. Widths already in percentages are left alone, and markup with
 * nothing to convert is returned untouched.
 * @function
 *
 * @param {string} html - The template fragment.
 * @param {number} [pageWidthPx] - The printable width the pixels are measured against.
 * @returns {string} The fragment with percentage widths.
 */
export const normalizeTableWidths = (
  html,
  pageWidthPx = PRINTABLE_WIDTH_PX
) => {
  if (typeof html !== 'string' || !html.trim()) return html;
  if (typeof DOMParser === 'undefined') return html;

  const document = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;

  document.body.querySelectorAll('table').forEach((table) => {
    const tableWidthPx = pixelWidth(table);
    // Cells are sized against their own table; against the page when the
    // table has no pixel width of its own.
    const cellReferencePx = tableWidthPx || pageWidthPx;

    if (tableWidthPx !== null) {
      applyPercentage(table, toPercentage(tableWidthPx, pageWidthPx));
      changed = true;
    }

    table.querySelectorAll('td, th, col').forEach((cell) => {
      if (cell.closest('table') !== table) return;
      const cellWidthPx = pixelWidth(cell);
      if (cellWidthPx === null) return;
      applyPercentage(cell, toPercentage(cellWidthPx, cellReferencePx));
      changed = true;
    });
  });

  return changed ? document.body.innerHTML : html;
};

/**
 * Applies the normalisations every part of a template (header, body, footer)
 * goes through right before it is saved.
 * @function
 *
 * @param {Object<string, string>} content - The template parts, keyed by name.
 * @returns {Object<string, string>} The same parts, ready to be stored.
 */
export const normalizeTemplateContent = (content) =>
  Object.fromEntries(
    Object.entries(content || {}).map(([part, html]) => [
      part,
      normalizeConditionMarkers(
        clampNegativeVerticalMargins(normalizeTableWidths(html))
      )
    ])
  );
